import type { FC, ReactNode } from "react";
import { useContext, useEffect, useState } from "react";
import { createContext } from "react";
import { EMarketBuilderTabs } from "./MarketBuilder.types";
import type { ICountryObject } from "app/services/CountryService";
import { useSelector } from "react-redux";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { getInitialSelectedCountry } from "./builderContextUtils";
import * as utils from "app/components/filters-bar/utils";

type IBuilderContext = {
    selectedTabIndex: EMarketBuilderTabs;
    setSelectedTabIndex: (tabIndex: EMarketBuilderTabs) => void;
    selectedCountry: ICountryObject;
    setSelectedCountry: (selectedCountry: ICountryObject) => void;
    showSegmentsDisclaimer: boolean;
    setShowSegmentsDisclaimer: (bool: boolean) => void;
    openAutocompleteOnStart: boolean;
};

export const BuilderContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { initialCountry, internalSource } = proNavigator.getParams();
    const [selectedTabIndex, setSelectedTabIndex] = useState(EMarketBuilderTabs.WEBSITE);
    const [selectedCountry, setSelectedCountry] = useState<ICountryObject>(
        getInitialSelectedCountry(initialCountry, utils.getCountries()),
    );
    const [showSegmentsDisclaimer, setShowSegmentsDisclaimer] = useState<boolean>(false);

    const { segmentsLoading } = useSelector((state) => {
        const {
            segmentsModule: { segmentsLoading },
        } = state;
        return { segmentsLoading };
    });
    const openAutocompleteOnStart = !internalSource && !segmentsLoading;
    useEffect(() => {
        if (!openAutocompleteOnStart) {
            return;
        }
        allTrackers.trackEvent("Autocomplete", "open", `By websites/AutocompleteOpen/`);
    }, []);

    return (
        <BuilderContext.Provider
            value={{
                selectedTabIndex,
                setSelectedTabIndex,
                selectedCountry,
                setSelectedCountry,
                showSegmentsDisclaimer,
                setShowSegmentsDisclaimer,
                openAutocompleteOnStart,
            }}
        >
            {children}
        </BuilderContext.Provider>
    );
};

const BuilderContext = createContext<IBuilderContext | null>(null);
export const useBuilderContext = (): IBuilderContext => useContext(BuilderContext);
